import authSession from "../lib/authSession";

import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../constants";
import "bootstrap/dist/css/bootstrap.min.css";
import Toast from "../components/Toast2";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toDisplayString } from "../utils/toDisplayString";
import { safeNext } from "../lib/roles";

// Should match the cooldown period on the backend.
const RESEND_COOLDOWN_SECONDS = 60;

const formatCooldownTime = (totalSeconds) => {
  if (totalSeconds <= 0) return "0s";
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const parts = [];
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || totalSeconds < 60) parts.push(`${seconds}s`);
  return parts.join(" ");
};

/**
 * Email verification page.
 *
 * There is no OTP flow in the API. Per the collection:
 *   • POST /api/auth/resend-verification  { email }  — emails a verification link
 *   • POST /api/auth/verify-email         { token }  — verifies the token
 *
 * The emailed link may return to this page as `/verify-email?token=…` (verified
 * here) or hit the API directly and redirect to `/login?verified=true`.
 */
const VerifyOtp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const emailFromUrl = searchParams.get("email") || "";
  const returnTo = safeNext(searchParams.get("next")) || safeNext(localStorage.getItem("fillop_auth_return_to"));

  // Prefer the email carried by the link (or the one just registered). Never
  // fall back to the shared `email` key, because admin/staff logins also write
  // it and it can show an unrelated address.
  const [userEmail, setUserEmail] = useState(
    emailFromUrl || localStorage.getItem("optEmail") || ""
  );
  const [status, setStatus] = useState(token ? "verifying" : "awaiting");
  const [message, setMessage] = useState(
    token ? "Verifying your email..." : "Check your inbox and click the verification link."
  );
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);
  const showToast = useCallback((text, success = true) => {
    setToast(text);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  // POST /api/auth/verify-email — { token }
  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    (async () => {
      try {
        const response = await authSession.authFetch(`${API_URL}/api/auth/verify-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = await response.json().catch(() => ({}));
        if (!active) return;
        if (response.ok && !data.error) {
          const verifiedEmail =
            data.email || data.user?.email || data.data?.user?.email || data.data?.email;
          if (verifiedEmail) setUserEmail(verifiedEmail);
          localStorage.removeItem("optEmail");
          setStatus("verified");
          setMessage(toDisplayString(data.message, "Your email has been verified."));
          showToast("Email verified successfully 🎉", true);

          const accessToken = authSession.findAccessToken(data);
          if (accessToken) {
            authSession.setSession({
              token: accessToken,
              role: "user",
              user: data.user || data.data?.user || null,
            });
            localStorage.setItem("isAdmin", "false");
            localStorage.removeItem("fillop_auth_return_to");
            setTimeout(() => navigate(returnTo || "/", { replace: true }), 1200);
          } else {
            const loginTarget = `/login?verified=true${returnTo ? `&next=${encodeURIComponent(returnTo)}` : ""}`;
            setTimeout(() => navigate(loginTarget, { replace: true }), 1500);
          }
        } else {
          setStatus("failed");
          setMessage(
            toDisplayString(
              data.error || data.message,
              "This verification link is invalid or has expired."
            )
          );
          showToast("Verification failed", false);
        }
      } catch {
        if (!active) return;
        setStatus("failed");
        setMessage("Unable to reach the server. Please try again.");
        showToast("Unable to reach the server", false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token, navigate, showToast, returnTo]);

  // Resend cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // POST /api/auth/resend-verification — { email }
  const handleResend = async () => {
    if (cooldown > 0 || isResending) return;
    if (!userEmail) {
      showToast("Enter the email you registered with", false);
      return;
    }
    setIsResending(true);
    try {
      const response = await authSession.authFetch(`${API_URL}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && !data.error) {
        setStatus("awaiting");
        setMessage(toDisplayString(data.message, "Verification email sent. Check your inbox."));
        setCooldown(RESEND_COOLDOWN_SECONDS);
        showToast("Verification email sent 🎉", true);
      } else {
        setMessage(
          toDisplayString(data.error || data.message, "Failed to send verification email.")
        );
        showToast("Failed to send verification email", false);
      }
    } catch {
      setMessage("Unable to reach the server. Please try again.");
      showToast("Unable to reach the server", false);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="limiter">
      {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}
      <div className="container vh-100 d-flex justify-content-center align-items-center">
        <div className="col-md-6 col-lg-5">
          <div className="card shadow-sm">
            <div className="card-body p-4 p-md-5">
              <h2 className="card-title text-center mb-2">Verify Your Email</h2>

              {status === "verifying" ? (
                <div className="d-flex justify-content-center align-items-center mb-4">
                  <div className="loader"></div>
                </div>
              ) : (
                <p className="card-subtitle text-center text-muted mb-4">{message}</p>
              )}

              {status === "verified" && (
                <div className="alert alert-success text-center mb-3">Your email is verified.</div>
              )}

              {status === "failed" && (
                <div className="alert alert-danger text-center mb-3">{message}</div>
              )}

              {status !== "verified" && (
                <>
                  <div className="mb-3">
                    <label htmlFor="verify-email" className="form-label">
                      Email address
                    </label>
                    <input
                      id="verify-email"
                      type="email"
                      className="form-control"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>

                  <div className="d-grid">
                    <button
                      type="button"
                      className="btn btn-primary btn-lg"
                      onClick={handleResend}
                      disabled={isResending || cooldown > 0}
                    >
                      {isResending
                        ? "Sending..."
                        : cooldown > 0
                          ? `Resend in ${formatCooldownTime(cooldown)}`
                          : "Resend verification email"}
                    </button>
                  </div>

                  <div className="text-center mt-3">
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => navigate("/login")}
                    >
                      Back to sign in
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
